import { useState } from "react";
import { Sparkles, Check, X, Loader2 } from "lucide-react";
import { api, formatErr, cx } from "../lib/api";
import { useFx } from "../lib/fx";
import { useI18n } from "../lib/i18n";

/**
 * Pending trip offer card shown to the user.
 * Props:
 * - offer: full offer document
 * - onChange(): refresh parent on accept/decline
 */
export default function TripOfferCard({ offer, onChange }) {
  const { t } = useI18n();
  const { format } = useFx();
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const respond = async (action) => {
    if (action === "decline" && !window.confirm(t("offer.decline_confirm", "Decline this offer?"))) return;
    setBusy(action); setError(null);
    try {
      await api.post(`/custom-trips/offers/${offer.id}/respond`, { action });
      onChange?.();
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(null); }
  };

  const isPending = offer.status === "pending";

  return (
    <div className={cx(
      "luxe-card p-6 border-teal/30 relative overflow-hidden",
      isPending && "ring-1 ring-teal/40"
    )} data-testid={`offer-card-${offer.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="eyebrow flex items-center gap-2 text-teal">
            <Sparkles size={12}/> {t("offer.eyebrow", "Concierge offer")} · {offer.status}
          </div>
          <div className="font-serif-display text-2xl mt-2">{offer.title}</div>
          {offer.summary && <div className="text-sm text-muted mt-1 max-w-xl">{offer.summary}</div>}
        </div>
        <div className="text-right">
          <div className="label-tiny">{t("offer.total", "Total")}</div>
          <div className="font-serif-display text-3xl">{format(offer.total_price, offer.currency)}</div>
          <div className="text-xs text-muted">
            {t("offer.deposit_required", "Deposit")}: {format(offer.deposit_required, offer.currency)}
          </div>
        </div>
      </div>

      <div className="space-y-2 mb-4">
        {offer.items.map((it, idx) => (
          <div key={idx} className="luxe-card p-3 border hairline" data-testid={`offer-item-${offer.id}-${idx}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-serif-display text-base truncate">{it.title}</div>
                <div className="text-xs text-muted mt-0.5">
                  {it.provider_name && <span>{it.provider_name} · </span>}
                  {it.start_date && <span>{it.start_date}{it.end_date ? ` → ${it.end_date}` : ""} · </span>}
                  {it.guests && <span>{it.guests} guests</span>}
                </div>
                {it.description && <div className="text-xs text-muted mt-1 line-clamp-2">{it.description}</div>}
              </div>
              <div className="text-right shrink-0">
                <div className="font-serif-display">{format(it.price, it.currency)}</div>
                {it.service_id && <div className="text-[10px] text-teal/70">linked</div>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {offer.note_to_user && (
        <div className="text-sm italic text-muted mb-4 border-l-2 border-teal/40 pl-3">
          "{offer.note_to_user}"
        </div>
      )}

      {error && <div className="text-rose-300 text-xs mb-3">{error}</div>}

      {isPending ? (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => respond("accept")} disabled={!!busy}
                  className="btn btn-primary" data-testid={`offer-accept-${offer.id}`}>
            {busy === "accept" ? <Loader2 size={14} className="animate-spin"/> : <Check size={14}/>}
            {t("offer.accept", "Accept & create bookings")}
          </button>
          <button onClick={() => respond("decline")} disabled={!!busy}
                  className="btn btn-ghost text-rose-300" data-testid={`offer-decline-${offer.id}`}>
            <X size={14}/> {t("offer.decline", "Decline")}
          </button>
        </div>
      ) : (
        <div className="text-xs text-muted">
          {t("offer.responded", "You {{status}} this offer.", { status: offer.status })}{" "}
          {offer.user_response_note && <span>· "{offer.user_response_note}"</span>}
        </div>
      )}
    </div>
  );
}
